// src/utils/impactScorecard.js
// Pure, Firebase-free helpers for the IMPACT Scorecard (2026 fillable PDF).
//   computeScorecardMetrics — turns the deal list into the scorecard's numbers.
//   buildScorecardFields    — maps scorecard state to the PDF's exact field names.
import { parseLocalDate, isOpen, notDeleted, ownedBy } from "./statusSheetData";
import { incomeForAgent } from "./dealCredit";
import { basisLabel } from "./knowYourNumbers";

const isClosedWon = (d) => String(d?.dealStage || "").toLowerCase() === "closed";

// A closed deal's effective close date: the stamped closedDate, else the
// contractual closingdate. parseLocalDate handles both ISO timestamps and
// "YYYY-MM-DD" calendar strings.
const closeDateOf = (d) => parseLocalDate(d?.closedDate || d?.closingdate);

const inMonth = (dt, year, month) =>
  !!dt && dt.getFullYear() === year && dt.getMonth() === month;

// Numbers for the scorecard.
//   "Last month"  = the calendar month before asOf — units/volume/gross income
//                   of deals CLOSED then.
//   "This month"  = OPEN deals whose closing date lands in asOf's month —
//                   the sheet's "Contracts (Closing this month) Locked Down MTD".
//   "Next month"  = OPEN deals closing the month after asOf — the sheet's
//                   "Contracts (Closing next month) Locked Down".
// agentId omitted/undefined → team-wide (ownedBy returns true for everyone).
export function computeScorecardMetrics(deals, { agentId, asOf = new Date() } = {}) {
  const base = new Date(asOf.getFullYear(), asOf.getMonth(), 1);
  const prev = new Date(base.getFullYear(), base.getMonth() - 1, 1);
  const next = new Date(base.getFullYear(), base.getMonth() + 1, 1);
  const list = Array.isArray(deals) ? deals : [];

  const closedLastMonth = list.filter(
    (d) =>
      isClosedWon(d) &&
      notDeleted(d) &&
      inMonth(closeDateOf(d), prev.getFullYear(), prev.getMonth()),
  );

  // Units and volume: one deal, one owner, so a sale is never counted twice.
  const creditedLastMonth = closedLastMonth.filter((d) => ownedBy(d, agentId));
  const unitsLastMonth = creditedLastMonth.length;
  const volumeLastMonth = creditedLastMonth.reduce(
    (sum, d) => sum + Number(d.offerPrice || 0),
    0,
  );
  // The 2026 sheet asks for GROSS income. Per agent that is his own commission
  // slice, never the other person's, which is what makes an in-house referral
  // read wrong if you just take agentCommissionamount. Dollars follow the
  // money, not the unit: the referring agent is paid his fee on a sale he is
  // not credited with, so he appears here too, and the slices add back up to
  // what the deal paid out rather than to more than it. incomeForAgent is the
  // same money rule Know Your Numbers uses, so the two printed sheets cannot
  // drift apart. It returns 0 for anyone the deal never paid, so no extra
  // ownership filter is needed. Every number stays editable in the tab.
  const grossIncomeLastMonth = closedLastMonth.reduce(
    (sum, d) => sum + incomeForAgent(d, agentId),
    0,
  );

  const openClosingIn = (year, month) =>
    list.filter(
      (d) =>
        isOpen(d) &&
        notDeleted(d) &&
        ownedBy(d, agentId) &&
        d.closingdate &&
        inMonth(parseLocalDate(d.closingdate), year, month),
    ).length;

  const contractsThisMonth = openClosingIn(base.getFullYear(), base.getMonth());
  const contractsNextMonth = openClosingIn(next.getFullYear(), next.getMonth());

  return {
    unitsLastMonth,
    volumeLastMonth,
    grossIncomeLastMonth,
    contractsThisMonth,
    contractsNextMonth,
  };
}

// ---- what each scorecard number is actually counting ----
//
// "Gross Income Last Month" means two different, both-correct things depending
// on the scope button, and the printed sheet cannot say which because its label
// is artwork inside the template PDF:
//
//   My Team -> the sum of each deal's GROSS commission, counted once per deal.
//              $9,666 + $11,814 = $21,480 for the two 1888 Walnut sides. This
//              is the defensible reading of a field called gross income, and it
//              is deliberately NOT the sum of the agent slices ($10,269.80),
//              which is what it used to be.
//   Me      -> the dollars that person was actually paid on deals closed last
//              month, added across every role he held. Henry $11,210.20 (two
//              team lead slices), Joe $10,269.80 (a referral fee plus an agent
//              slice). The per-person figures add back up to the team figure.
//
// Units and Volume are a different basis on purpose: one deal, one owner. So in
// the Me view a person can show 1 unit and $425,000 of volume above $11,210.20
// of income, because some of that income came off a sale credited to somebody
// else. These notes say so on screen. They change no number.
export const SCORECARD_BASIS_NOTES = {
  me: {
    unitsLastMonth: "Your own closings last month. A sale counts once, for the agent it belongs to.",
    volumeLastMonth: "Sale price of your own closings last month.",
    grossIncomeLastMonth:
      "Everything you were paid on deals that closed last month, including your team lead share of other agents' closings.",
    contractsThisMonth: "Your own open deals set to close this month.",
    contractsNextMonth: "Your own open deals set to close next month.",
  },
  team: {
    unitsLastMonth: "Every closing on the team last month, counted once per deal.",
    volumeLastMonth: "Sale price of every closing on the team last month.",
    grossIncomeLastMonth:
      "The team's total commission on every deal closed last month, counted once per deal. Not the sum of the agents' take-home slices.",
    contractsThisMonth: "Every open deal on the team set to close this month.",
    contractsNextMonth: "Every open deal on the team set to close next month.",
  },
};

// The note for one field. Unknown scope falls back to the personal wording.
export function scorecardBasisNote(field, scope = "me") {
  const set = SCORECARD_BASIS_NOTES[scope === "team" ? "team" : "me"];
  return set[field] || "";
}

// Which basis each scorecard figure stands on, and the words that say so in the
// label itself. Gross Income is the earned basis (everything the person was
// paid); Units and Volume are the credited basis (his own closings). The
// parentheticals come from knowYourNumbers so the SAME figure is worded the
// same way on both printed sheets: "Income YTD (everything paid to you)" there,
// "Gross Income Last Month (everything paid to you)" here.
export const SCORECARD_FIELD_BASIS = {
  unitsLastMonth: "credited",
  volumeLastMonth: "credited",
  grossIncomeLastMonth: "earned",
};

const SCORECARD_FIELD_NAMES = {
  unitsLastMonth: "Units Last Month",
  volumeLastMonth: "Volume Last Month",
  grossIncomeLastMonth: "Gross Income Last Month",
};

// The on-screen label for one figure: its printed name plus the basis it stands
// on. Unknown field returns "".
export function scorecardFieldLabel(field, scope = "me") {
  const name = SCORECARD_FIELD_NAMES[field];
  if (!name) return "";
  const suffix = basisLabel(SCORECARD_FIELD_BASIS[field], scope);
  return suffix ? `${name} ${suffix}` : name;
}

// The printed "Current Quarter Goals #3" gets two lines on the sheet ("3 1" and
// "3 2"). Split long text at a word boundary so nothing is cut off in print.
const LINE_MAX = 80;
export function splitTwoLines(text, max = LINE_MAX) {
  const s = String(text == null ? "" : text).trim();
  if (s.length <= max) return [s, ""];
  let cut = s.lastIndexOf(" ", max);
  if (cut <= 0) cut = max;
  return [s.slice(0, cut).trim(), s.slice(cut).trim()];
}

// Map scorecard state to the 2026 PDF's AcroForm field names. Every value is
// coerced to a string so unset fields clear (never write `undefined`).
//
// Field-name legend (the PDF generator numbered repeated "1." labels):
//   Current Quarter Goals →  "1", "2", "3 1" + "3 2" (goal #3 wraps to 2 lines)
//   Top 4 Goals for Year  →  "1_2", "2_2", "3", "4"
//   Top 2 Personal Goals  →  "1_3", "2_3"
//   Scorecard column      →  "MY SCORECARD" (units), "undefined" (volume),
//                            gross income, savings, then two actual/required
//                            contract pairs: MTD ("Contracts Locked Down MTD" /
//                            "undefined_2") and Next Month ("Contracts Locked
//                            Down Next Month" / "undefined_3").
// Verified against the rendered PDF (widget geometry + pdftotext) 2026-07-15 —
// NOTE: the pre-2026 mapping had the three goal blocks scrambled; this one is
// confirmed by a filled visual proof.
export function buildScorecardFields(v = {}) {
  const s = (x) => (x == null ? "" : String(x));
  const year = v.yearGoals || [];
  const personal = v.personalGoals || [];
  const quarter = v.quarterGoals || [];
  const [q3a, q3b] = splitTwoLines(quarter[2]);
  return {
    Name: s(v.name),
    "Coaching Call Date": s(v.coachingCallDate),
    "My Why": s(v.why),
    "MY SCORECARD": s(v.units),
    undefined: s(v.volume),
    "Gross Income Last Month 2nd call": s(v.grossIncome),
    "Savings Last Month 2nd call": s(v.savings),
    "Contracts Locked Down MTD": s(v.contractsMtdActual),
    undefined_2: s(v.contractsMtdRequired),
    "Contracts Locked Down Next Month": s(v.contractsNextActual),
    undefined_3: s(v.contractsNextRequired),
    1: s(quarter[0]),
    2: s(quarter[1]),
    "3 1": s(q3a),
    "3 2": s(q3b),
    "1_2": s(year[0]),
    "2_2": s(year[1]),
    3: s(year[2]),
    4: s(year[3]),
    "1_3": s(personal[0]),
    "2_3": s(personal[1]),
    "Accountability Choice": s(v.accountability),
  };
}
